import React, { FC, useState } from "react";
import {
  asDto,
  BaseWidgetProps,
  ItemBoxConfiguration,
  KeyInformationWidget,
  useKeyInformationData,
} from "shared-ui";
import { Button, MenuItem, Select } from "@mui/material";
import { AccessAlarm, AlarmOff, AlarmOn } from "@mui/icons-material";
import { LifecycleManagementClient } from "@lib/ShiOneClient";
import { useApi } from "@utils/useApi";
import { useNavigate } from "react-router-dom";

interface UpcomingLifecycleEventsResponseDto {
  totalEvents?: number;
  eventsActioned?: number;
  eventsNotActioned?: number;
  isError?: boolean;
}

const termMonthsOptions = [
  { label: "Next 30 days", value: 1 },
  { label: "Next 90 days", value: 3 },
  { label: "Next 6 Months", value: 6 },
  { label: "Next 12 Months", value: 12 },
];

const placeholderData = {
  success: true,
  isEmptyDataSet: false,
  data: {
    totalEvents: 0,
    eventsActioned: 0,
    eventsNotActioned: 0,
  },
};

function transformFunction(
  rawData: UpcomingLifecycleEventsResponseDto | undefined,
): ItemBoxConfiguration[] {
  return [
    {
      amount: rawData?.totalEvents ?? 0,
      label: "Total Events",
      icon: <AccessAlarm fontSize="large" />,
    },
    {
      amount: rawData?.eventsActioned ?? 0,
      label: "Events Actioned",
      icon: <AlarmOn fontSize="large" color="success" />,
    },
    {
      amount: rawData?.eventsNotActioned ?? 0,
      label: "Events Not Actioned",
      icon: <AlarmOff fontSize="large" color="error" />,
    },
  ];
}

const UpcomingLifecycleEventsWidget: FC<BaseWidgetProps> = ({
  pageLayoutWidget,
}) => {
  const navigate = useNavigate();
  const [termMonths, setTermMonths] = useState(12);

  const useGetUpcomingLifecycleEventsData = () => {
    const api = useApi(LifecycleManagementClient);

    return useKeyInformationData<UpcomingLifecycleEventsResponseDto>({
      queryKey: ["upcoming-lifecycle-events-widget", termMonths],
      apiFunction: () => api.getEventCountsByTerm(termMonths),
      transformFunction: transformFunction,
      placeholderData: asDto(placeholderData.data),
    });
  };

  const headerAction = () => {
    return (
      <Select
        sx={{ height: "40px" }}
        labelId="TermSelector"
        id="TermSelector"
        value={termMonths}
        onChange={(event) => {
          setTermMonths(
            Number(event.target.value) ? Number(event.target.value) : 12,
          );
        }}
        autoWidth
        displayEmpty
      >
        {termMonthsOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    );
  };

  const handleViewClick = () => {
    navigate("/assets/lifecycle-management?tab=event-timeline");
  };

  const cardActions = () => {
    return (
      <Button variant="text" color="primary" onClick={handleViewClick}>
        View Lifecycle Timeline
      </Button>
    );
  };

  return (
    <KeyInformationWidget
      pageLayoutWidget={pageLayoutWidget}
      useQueryFunction={useGetUpcomingLifecycleEventsData}
      cardActions={cardActions()}
      headerAction={headerAction()}
    />
  );
};

export default UpcomingLifecycleEventsWidget;
